import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Brand, FacebookAd } from "@/hooks/api/use-brand-ads";
import { cn } from "@/lib/utils";
import {
  ExternalLink,
  Facebook,
  Globe,
  Instagram,
  MessageCircleMore,
  PlayCircle,
  Sparkles,
} from "lucide-react";
import { useState } from "react";

interface AdCardProps {
  ad: FacebookAd;
  brand?: Brand;
  onAnalysisClick?: (analysis: string) => void;
  className?: string;
}

export default function AdCard({
  ad,
  brand,
  onAnalysisClick,
  className,
}: AdCardProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  if (!ad.latest_snapshot) return null;

  return (
    <Card className={cn("overflow-hidden", className)}>
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-3">
          {brand ? (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center overflow-hidden">
                {brand.profile_photo ? (
                  <img
                    src={brand.profile_photo}
                    alt={`${brand.name} profile`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      e.currentTarget.nextElementSibling?.removeAttribute(
                        "style"
                      );
                    }}
                  />
                ) : (
                  <span
                    className="text-2xl font-semibold"
                    style={{
                      display: "block",
                    }}
                  >
                    {brand.name?.[0]?.toUpperCase() || ""}
                  </span>
                )}
              </div>
              <div>
                <p className="font-medium text-sm">{brand.name}</p>
                <div className="flex items-center gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1.5">
                          <div
                            className={`w-1.5 h-1.5 rounded-full ${
                              ad.is_active ? "bg-green-500" : "bg-red-500"
                            }`}
                          />
                          <span className="text-xs text-muted-foreground">
                            {ad.is_active
                              ? `Active for ${Math.ceil(
                                  (Date.now() -
                                    new Date(
                                      ad.start_date || Date.now()
                                    ).getTime()) /
                                    (24 * 60 * 60 * 1000)
                                )}d`
                              : `Ran for ${Math.ceil(
                                  (new Date(
                                    ad.end_date || Date.now()
                                  ).getTime() -
                                    new Date(
                                      ad.start_date ||
                                        new Date(
                                          ad.end_date || Date.now()
                                        ).getTime()
                                    ).getTime()) /
                                    (24 * 60 * 60 * 1000)
                                )}d`}
                          </span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">
                          Started:{" "}
                          {new Date(ad.start_date!).toLocaleDateString()}
                          {ad.end_date && (
                            <>
                              <br />
                              Ended:{" "}
                              {new Date(ad.end_date).toLocaleDateString()}
                            </>
                          )}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5">
                <div
                  className={`w-1.5 h-1.5 rounded-full ${
                    ad.is_active ? "bg-green-500" : "bg-red-500"
                  }`}
                />
                <span className="text-xs text-muted-foreground">
                  {ad.is_active
                    ? `Active for ${Math.ceil(
                        (Date.now() -
                          new Date(ad.start_date || Date.now()).getTime()) /
                          (24 * 60 * 60 * 1000)
                      )}d`
                    : `Ran for ${Math.ceil(
                        (new Date(ad.end_date || Date.now()).getTime() -
                          new Date(
                            ad.start_date ||
                              new Date(ad.end_date || Date.now()).getTime()
                          ).getTime()) /
                          (24 * 60 * 60 * 1000)
                      )}d`}
                </span>
              </div>
            </div>
          )}
          {ad.latest_analysis && onAnalysisClick && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() =>
                      onAnalysisClick(ad.latest_analysis?.overview || "")
                    }
                  >
                    <Sparkles className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View AI Analysis</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="text-sm mb-3 line-clamp-2 cursor-default">
                {ad.latest_snapshot?.cards[0]?.body_text ||
                  ad.latest_snapshot?.body_text}
              </p>
            </TooltipTrigger>
            <TooltipContent className="max-w-md">
              <p className="text-sm">
                {ad.latest_snapshot?.cards[0]?.body_text ||
                  ad.latest_snapshot?.body_text}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="flex flex-col items-center justify-center relative">
        {ad.latest_snapshot && (
          <>
            <div className="w-full flex-1 flex items-center justify-center">
              {ad.latest_snapshot.cards[cardIndex]?.video_hd_url ||
              (!ad.latest_snapshot.cards.length &&
                ad.latest_snapshot.video_hd_url) ? (
                <div className="relative min-h-[300px] flex items-center justify-center w-full">
                  {!isPlaying ? (
                    <div
                      className="relative cursor-pointer group w-full"
                      onClick={() => setIsPlaying(true)}
                    >
                      <img
                        src={
                          ad.latest_snapshot.cards[cardIndex]
                            ?.video_preview_image_url ||
                          ad.latest_snapshot.video_preview_image_url ||
                          ""
                        }
                        alt="Video preview"
                        className="w-full max-h-[500px] object-contain"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-black/50 flex items-center justify-center group-hover:bg-black/70 transition-colors">
                          <PlayCircle className="w-8 h-8 text-white" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <video
                      className="w-full max-h-[500px] object-contain"
                      controls
                      autoPlay
                    >
                      {ad.latest_snapshot.cards[cardIndex]?.video_hd_url && (
                        <source
                          src={
                            ad.latest_snapshot.cards[cardIndex].video_hd_url ||
                            ""
                          }
                          type="video/mp4"
                        />
                      )}
                      {!ad.latest_snapshot.cards.length &&
                        ad.latest_snapshot?.video_hd_url && (
                          <source
                            src={ad.latest_snapshot.video_hd_url || ""}
                            type="video/mp4"
                          />
                        )}
                    </video>
                  )}
                </div>
              ) : (
                (ad.latest_snapshot.cards[cardIndex]?.resized_image_url ||
                  ad.latest_snapshot.cards[cardIndex]?.original_image_url ||
                  ad.latest_snapshot.cards[cardIndex]
                    ?.video_preview_image_url ||
                  (!ad.latest_snapshot.cards.length &&
                    (ad.latest_snapshot.resized_image_url ||
                      ad.latest_snapshot.original_image_url ||
                      ad.latest_snapshot.video_preview_image_url))) && (
                  <img
                    src={
                      ad.latest_snapshot.cards[cardIndex]?.resized_image_url ||
                      ad.latest_snapshot.cards[cardIndex]?.original_image_url ||
                      ad.latest_snapshot.cards[cardIndex]
                        ?.video_preview_image_url ||
                      ad.latest_snapshot.resized_image_url ||
                      ad.latest_snapshot.original_image_url ||
                      ad.latest_snapshot.video_preview_image_url ||
                      ""
                    }
                    alt="Ad"
                    className="w-full max-h-[500px] object-contain"
                  />
                )
              )}
            </div>

            {ad.latest_snapshot?.cards?.length > 1 && (
              <div className="w-full mt-2 flex justify-center">
                <div className="flex items-center gap-1 p-1 bg-muted rounded-md overflow-x-auto max-w-full">
                  {ad.latest_snapshot.cards.map((card, index) => (
                    <div
                      key={index}
                      className={`w-12 h-12 cursor-pointer overflow-hidden rounded-sm border-2 ${
                        index === cardIndex
                          ? "border-primary"
                          : "border-transparent"
                      }`}
                      onClick={() => {
                        setCardIndex(index);
                        if (isPlaying) {
                          setIsPlaying(false);
                        }
                      }}
                    >
                      <div className="relative w-full h-full">
                        <img
                          src={
                            card.video_preview_image_url ||
                            card.resized_image_url ||
                            card.original_image_url ||
                            ""
                          }
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        {card.video_hd_url && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                            <PlayCircle className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="p-4 bg-muted">
        {ad.latest_snapshot?.cta_text && ad.latest_snapshot?.link_url && (
          <a
            href={ad.latest_snapshot.link_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            {ad.latest_snapshot.cta_text}
          </a>
        )}
        <div className="mt-3 text-xs text-muted-foreground space-y-1">
          {ad.latest_snapshot?.display_format_display && (
            <div className="flex items-center gap-2">
              <span className="font-medium">Format:</span>
              <span>{ad.latest_snapshot.display_format_display}</span>
            </div>
          )}
          {ad.publisher_platforms.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="font-medium">Platforms:</span>
              <div className="flex items-center gap-1.5">
                {ad.publisher_platforms.map((platform) => {
                  const Icon =
                    platform.toLowerCase() === "facebook"
                      ? Facebook
                      : platform.toLowerCase() === "instagram"
                      ? Instagram
                      : platform.toLowerCase() === "messenger"
                      ? MessageCircleMore
                      : Globe;
                  return (
                    <TooltipProvider key={platform}>
                      <Tooltip>
                        <TooltipTrigger>
                          <Icon className="w-4 h-4" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{platform}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })}
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="font-medium">Ad ID:</span>
            <span className="text-xs">{ad.external_ad_id}</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 ml-auto p-0 cursor-pointer"
                    asChild
                  >
                    <a
                      href={`https://www.facebook.com/ads/library/?id=${ad.external_ad_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">View in Ad Library</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>
    </Card>
  );
}
